import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import {
  useLazyGetCategoryQuery,
  useLazyGetCategoryByIdQuery,
} from "../services/category";

const LIMIT = 20;
const STALE_TIME = 5 * 60 * 1000;
const GC_TIME = 10 * 60 * 1000;

function parseCategoryListResponse(res: { statusCode?: number; data?: { list?: unknown[]; totalCount?: number } }) {
  if (res?.statusCode === 200 && res?.data) {
    return {
      list: res.data.list ?? [],
      totalCount: res.data.totalCount ?? 0,
    };
  }
  return { list: [], totalCount: 0 };
}

export const categoryQueryKeys = {
  list: (page: number, limit: number, search: string) =>
    ["category", "list", page, limit, search] as const,
  infiniteList: (search: string) => ["category", "list", search] as const,
  byId: (id: string) => ["category", "byId", id] as const,
};

export function useCategoryListQuery(
  page: number,
  limit: number,
  search: string,
  enabled: boolean
) {
  const [getCategory] = useLazyGetCategoryQuery();

  return useQuery({
    queryKey: categoryQueryKeys.list(page, limit, search),
    queryFn: () =>
      getCategory({ page, limit, search })
        .unwrap()
        .then(parseCategoryListResponse),
    enabled,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useCategoryInfiniteListQuery(
  search: string,
  enabled: boolean
) {
  const [getCategory] = useLazyGetCategoryQuery();

  return useInfiniteQuery({
    queryKey: categoryQueryKeys.infiniteList(search),
    queryFn: ({ pageParam }) =>
      getCategory({
        page: pageParam,
        limit: LIMIT,
        search,
      })
        .unwrap()
        .then(parseCategoryListResponse),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, p) => sum + p.list.length, 0);
      return fetched < lastPage.totalCount ? allPages.length + 1 : undefined;
    },
    enabled,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useCategoryByIdQuery(id: string | undefined, enabled: boolean) {
  const [getCategoryById] = useLazyGetCategoryByIdQuery();

  return useQuery({
    queryKey: categoryQueryKeys.byId(id ?? ""),
    queryFn: () =>
      getCategoryById({ id: id! })
        .unwrap()
        .then((res) => {
          if (res?.statusCode === 200 && res?.data) return res.data;
          throw new Error("Failed to fetch category");
        }),
    enabled: !!id && enabled,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}
